import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../../../AuthContext';
import { db } from '../../../../firebase';
import {
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    serverTimestamp,
    updateDoc,
    doc
} from 'firebase/firestore';
import './VideoTab.css';

const INITIAL_FORM_STATE = {
    title: '',
    description: '',
    vimeoUrl: '',
    bunnyVideoId: '',
    price: '',
    thumbnail: '',
    isActive: true
};

const VideoTab = () => {
    const { isAdmin, user } = useContext(AuthContext);
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [showVideosModal, setShowVideosModal] = useState(false);

    useEffect(() => {
        const videosRef = collection(db, 'video_feed');
        const q = query(videosRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const videoList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setVideos(videoList);
        }, (err) => {
            console.error('Error fetching videos:', err);
        });

        return () => unsubscribe();
    }, []);

    if (!isAdmin) {
        return (
            <div className="video-tab">
                <h3>Videos</h3>
                <p className="video-tab__notice">Admin access required.</p>
            </div>
        );
    }

    const extractVimeoId = (url) => {
        if (!url) return '';
        const match = url.match(/(\d+)/);
        return match ? match[0] : url.trim();
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError(null);
        setSuccess('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess('');

        if (!formData.title.trim()) {
            setError('Title is required.');
            return;
        }

        if (!formData.vimeoUrl.trim() && !formData.bunnyVideoId.trim()) {
            setError('Provide a Bunny Video ID or a Vimeo URL/ID.');
            return;
        }

        if (!formData.price || Number.isNaN(parseFloat(formData.price))) {
            setError('Price must be a valid number.');
            return;
        }

        const videoPayload = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            price: parseFloat(formData.price),
            thumbnail: formData.thumbnail.trim() || null,
            isActive: !!formData.isActive,
            createdAt: serverTimestamp(),
            createdBy: user?.uid || null
        };

        if (formData.bunnyVideoId.trim()) {
            videoPayload.bunnyVideoId = formData.bunnyVideoId.trim();
        }

        if (formData.vimeoUrl.trim()) {
            videoPayload.vimeoId = extractVimeoId(formData.vimeoUrl);
            videoPayload.vimeoUrl = formData.vimeoUrl.trim();
        }

        try {
            setLoading(true);
            await addDoc(collection(db, 'video_feed'), videoPayload);
            setSuccess('Video post created successfully.');
            setFormData(INITIAL_FORM_STATE);
        } catch (err) {
            console.error('Error creating video post:', err);
            setError(err.message || 'Failed to create video post.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (videoId, currentState) => {
        try {
            await updateDoc(doc(db, 'video_feed', videoId), {
                isActive: !currentState,
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error('Error updating video:', err);
            setError('Failed to update video status.');
        }
    };

    return (
        <div className="video-tab">
            <h3>Videos</h3>

            <form className="video-form" onSubmit={handleSubmit}>
                <div className="video-form__group">
                    <label htmlFor="title">Title *</label>
                    <input
                        id="title"
                        name="title"
                        type="text"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Enter video title"
                        required
                    />
                </div>

                <div className="video-form__group">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Short description"
                        rows={3}
                    />
                </div>

                <div className="video-form__group">
                    <label htmlFor="bunnyVideoId">Bunny Video ID</label>
                    <input
                        id="bunnyVideoId"
                        name="bunnyVideoId"
                        type="text"
                        value={formData.bunnyVideoId}
                        onChange={handleChange}
                        placeholder="Video ID from Bunny Stream"
                    />
                </div>

                <div className="video-form__group">
                    <label htmlFor="vimeoUrl">Vimeo URL or ID</label>
                    <input
                        id="vimeoUrl"
                        name="vimeoUrl"
                        type="text"
                        value={formData.vimeoUrl}
                        onChange={handleChange}
                        placeholder="https://vimeo.com/123456789 or 123456789"
                    />
                </div>

                <div className="video-form__row">
                    <div className="video-form__group">
                        <label htmlFor="price">Price (USD) *</label>
                        <input
                            id="price"
                            name="price"
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.price}
                            onChange={handleChange}
                            placeholder="e.g. 9.99"
                            required
                        />
                    </div>

                </div>

                <div className="video-form__group video-form__group--checkbox">
                    <label>
                        <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleChange}
                        />
                        {' '}Video is active (visible to users)
                    </label>
                </div>

                {error && <p className="video-form__error">{error}</p>}
                {success && <p className="video-form__success">{success}</p>}

                <button className="video-form__submit" type="submit" disabled={loading}>
                    {loading ? 'Creating...' : 'Create Video Post'}
                </button>
            </form>

            <button
                type="button"
                className="video-tab__open-modal"
                onClick={() => setShowVideosModal(true)}
            >
                View Existing Videos ({videos.length})
            </button>

            {showVideosModal && (
                <div className="video-modal-backdrop" onClick={() => setShowVideosModal(false)}>
                    <div className="video-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="video-modal__header">
                            <h4>Existing Videos</h4>
                            <button
                                type="button"
                                className="video-modal__close"
                                onClick={() => setShowVideosModal(false)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="video-modal__body">
                            {videos.length === 0 ? (
                                <p className="video-list__empty">No videos created yet.</p>
                            ) : (
                                <div className="video-modal__table-wrap">
                                    <table className="video-table">
                                        <thead>
                                            <tr>
                                                <th>Title</th>
                                                <th>Price</th>
                                                <th>Purchases</th>
                                                <th>Bunny ID</th>
                                                <th>Vimeo ID</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {videos.map(video => (
                                                <tr key={video.id}>
                                                    <td>{video.title || 'Untitled'}</td>
                                                    <td>${Number(video.price || 0).toFixed(2)}</td>
                                                    <td>{(video.purchaserEmails || []).length}</td>
                                                    <td>{video.bunnyVideoId || 'N/A'}</td>
                                                    <td>{video.vimeoId || video.vimeoUrl || 'N/A'}</td>
                                                    <td>
                                                        <span className={`video-status ${video.isActive ? 'video-status--active' : 'video-status--inactive'}`}>
                                                            {video.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="video-table__action"
                                                            onClick={() => handleToggleActive(video.id, video.isActive)}
                                                        >
                                                            {video.isActive ? 'Deactivate' : 'Activate'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VideoTab;
